import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaSearch } from "react-icons/fa";
import { WiTime2 } from "react-icons/wi";

// Main InviteUserModal Component
const InviteUserModal = ({ isOpen, onClose, type, onAddUser }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [addingUsers, setAddingUsers] = useState(false);
    const [offset, setOffset] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [initialFetchDone, setInitialFetchDone] = useState(false);

    // State for multiple selection
    const [selectedUsers, setSelectedUsers] = useState([]);

    const objectId = localStorage.getItem("objectId");

    // Fetch users from API
    const fetchUsers = async (query, offsetValue = 1, isLoadMore = false) => {
        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/v1/usercontact/connection/search-get-user/`,
                {
                    params: {
                        searchParam: query || 'a',
                        limit: 20,
                        offset: offsetValue,
                    },
                    headers: {
                        Authorization: `Bearer ${objectId}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response?.data?.status === "SUCCESS") {
                const newResults = response?.data?.data || [];
                setHasMore(newResults?.length === 20);

                if (isLoadMore) {
                    setUsers((prev) => [...prev, ...newResults]);
                    setFilteredUsers((prev) => [...prev, ...newResults]);
                } else {
                    setUsers(newResults);
                    setFilteredUsers(newResults);
                }
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Initial fetch when modal opens
    useEffect(() => {
        if (isOpen && !initialFetchDone) {
            fetchUsers('a', 1, false);
            setInitialFetchDone(true);
        } else if (!isOpen) {
            // Reset states when modal closes
            setSearchTerm('');
            setUsers([]);
            setFilteredUsers([]);
            setOffset(1);
            setHasMore(true);
            setInitialFetchDone(false);
            setAddingUsers(false);
            setSelectedUsers([]);
        }
    }, [isOpen]);

    // Debounced search
    useEffect(() => {
        const debounceSearch = setTimeout(() => {
            if (isOpen) {
                setOffset(1);
                setHasMore(true);
                if (searchTerm.trim() !== "") {
                    fetchUsers(searchTerm, 1, false);
                } else {
                    fetchUsers('a', 1, false);
                }
            }
        }, 300);

        return () => clearTimeout(debounceSearch);
    }, [searchTerm, isOpen]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;

        if (isNearBottom && !loadingMore && hasMore && isOpen) {
            const nextOffset = offset + 1;
            setOffset(nextOffset);
            fetchUsers(searchTerm || 'a', nextOffset, true);
        }
    };

    // Handle checkbox selection
    const handleCheckboxChange = (user) => {
        const userId = user.reference_id || user.id;
        setSelectedUsers(prev => {
            const isSelected = prev.some(u => (u.reference_id || u.id) === userId);
            if (isSelected) {
                return prev.filter(u => (u.reference_id || u.id) !== userId);
            } else {
                return [...prev, user];
            }
        });
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers([...filteredUsers]);
        }
    };

    // Handle invite button click - Direct API call
    const handleInviteClick = async () => {
        if (selectedUsers.length === 0) {
            alert('Please select at least one user to invite');
            return;
        }

        setAddingUsers(true);

        try {
            // Call onAddUser with the array of selected users
            await onAddUser(selectedUsers, type);
            
            // Reset and close on success
            setSelectedUsers([]);
            onClose();
        } catch (error) {
            console.error("Error inviting users:", error);
            alert('Failed to invite users. Please try again.');
        } finally {
            setAddingUsers(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const primaryBlue = "#0044C6";

    if (!isOpen) return null;

    return (
        <>
            {/* Main Modal */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000,
                    backdropFilter: "blur(5px)",
                }}
                onClick={handleClose}
            >
                <div
                    style={{
                        background: "#FFFFFF",
                        borderRadius: "24px",
                        width: "90%",
                        maxWidth: "700px",
                        maxHeight: "90vh",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                        animation: "slideIn 0.3s ease-out",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Modal Header - Fixed */}
                    <div
                        style={{
                            padding: "1.5rem 2rem",
                            borderBottom: "1px solid #E9EDF4",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: "#F8FAFE",
                            flexShrink: 0,
                        }}
                    >
                        <h2
                            style={{
                                fontSize: "1.3rem",
                                fontWeight: 600,
                                color: "#0B1C33",
                                margin: 0,
                                textTransform: "capitalize",
                            }}
                        >
                            Select Users to Invite as {type}(s)
                        </h2>
                        <button
                            onClick={handleClose}
                            style={{
                                background: "none",
                                border: "none",
                                fontSize: "1.5rem",
                                cursor: "pointer",
                                color: "#6F7D95",
                                padding: "0.5rem",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#E9EDF4";
                                e.currentTarget.style.color = "#0B1C33";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "none";
                                e.currentTarget.style.color = "#6F7D95";
                            }}
                        >
                            ✕
                        </button>
                    </div>

                    {/* Search Bar - Fixed */}
                    <div
                        style={{
                            padding: "1.5rem 2rem",
                            borderBottom: "1px solid #E9EDF4",
                            flexShrink: 0,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                background: "#F5F7FA",
                                borderRadius: "40px",
                                padding: "0.5rem 1rem",
                                border: "1px solid #E9EDF4",
                                transition: "all 0.2s",
                            }}
                        >
                            <span style={{ color: "#6F7D95", marginRight: "0.5rem" }}><FaSearch /></span>
                            <input
                                type="text"
                                placeholder="Search by name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    width: "100%",
                                    fontSize: "0.95rem",
                                    padding: "0.5rem 0",
                                    outline: "none",
                                    color: "#0B1C33",
                                }}
                                autoFocus
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        color: "#6F7D95",
                                        cursor: "pointer",
                                        fontSize: "1rem",
                                        padding: "0.25rem 0.5rem",
                                        borderRadius: "50%",
                                    }}
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Select All Header - Fixed */}
                    {filteredUsers.length > 0 && (
                        <div
                            style={{
                                padding: "0.8rem 2rem",
                                background: "#F0F4FF",
                                borderBottom: "1px solid #E9EDF4",
                                display: "flex",
                                alignItems: "center",
                                gap: "1rem",
                                flexShrink: 0,
                            }}
                        >
                            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                    onChange={handleSelectAll}
                                    style={{
                                        width: "18px",
                                        height: "18px",
                                        cursor: "pointer",
                                        accentColor: primaryBlue,
                                    }}
                                />
                                <span style={{ fontWeight: 500, color: "#0B1C33" }}>
                                    Select All ({selectedUsers.length} selected)
                                </span>
                            </label>
                        </div>
                    )}

                    {/* User List - Scrollable */}
                    <div
                        style={{
                            padding: "1rem 2rem",
                            overflowY: "auto",
                            flex: 1,
                            minHeight: "200px",
                        }}
                        onScroll={handleScroll}
                    >
                        {loading ? (
                            <div style={{ textAlign: "center", padding: "3rem" }}>
                                <div style={{ color: primaryBlue, fontSize: "1rem" }}>Loading users...</div>
                            </div>
                        ) : filteredUsers.length > 0 ? (
                            <>
                                {filteredUsers.map((user, index) => (
                                    <div
                                        key={user.reference_id || user.id || index}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "1rem",
                                            borderBottom: index < filteredUsers.length - 1 ? "1px solid #EFF2F7" : "none",
                                            transition: "all 0.2s",
                                            borderRadius: "12px",
                                            marginBottom: "0.5rem",
                                            background: selectedUsers.some(u => (u.reference_id || u.id) === (user.reference_id || user.id))
                                                ? "#F0F4FF"
                                                : "transparent",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = "#F8FAFE";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = selectedUsers.some(u => (u.reference_id || u.id) === (user.reference_id || user.id))
                                                ? "#F0F4FF"
                                                : "transparent";
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "1rem", flex: 1 }}>
                                            {/* Checkbox */}
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.some(u => (u.reference_id || u.id) === (user.reference_id || user.id))}
                                                onChange={() => handleCheckboxChange(user)}
                                                style={{
                                                    width: "18px",
                                                    height: "18px",
                                                    cursor: "pointer",
                                                    accentColor: primaryBlue,
                                                }}
                                            />

                                            {/* Profile Picture */}
                                            <div
                                                style={{
                                                    width: "48px",
                                                    height: "48px",
                                                    borderRadius: "50%",
                                                    overflow: "hidden",
                                                    background: "#E9EDF4",
                                                    border: "2px solid #FFFFFF",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                    position: "relative",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <img
                                                    src={user.profile_url || "https://via.placeholder.com/48x48?text=User"}
                                                    alt={`${user.first_name} ${user.last_name}`}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                    }}
                                                    onError={(e) => {
                                                        e.target.src = "https://via.placeholder.com/48x48?text=User";
                                                    }}
                                                />
                                                {user.is_verified && (
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            bottom: "2px",
                                                            right: "2px",
                                                            width: "16px",
                                                            height: "16px",
                                                            background: "#0F9D58",
                                                            color: "white",
                                                            borderRadius: "50%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            border: "2px solid white",
                                                            fontSize: "10px",
                                                            fontWeight: "bold",
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                        }}
                                                    >
                                                        ✓
                                                    </div>
                                                )}
                                            </div>

                                            {/* User Info */}
                                            <div style={{ flex: 1 }}>
                                                <div style={{
                                                    fontWeight: 600,
                                                    color: "#0B1C33",
                                                    marginBottom: "0.2rem",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.5rem"
                                                }}>
                                                    {user.first_name} {user.last_name}
                                                </div>
                                                <div style={{ fontSize: "0.85rem", color: "#0044C6", marginBottom: "0.2rem" }}>
                                                    {user.email || 'Email not available'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Loading More Indicator */}
                                {loadingMore && (
                                    <div style={{ textAlign: "center", padding: "1rem" }}>
                                        <div style={{ color: primaryBlue, fontSize: "0.9rem" }}>Loading more users...</div>
                                    </div>
                                )}

                                {/* No More Results */}
                                {!hasMore && filteredUsers.length > 0 && (
                                    <div style={{ textAlign: "center", padding: "1rem", color: "#6F7D95" }}>
                                        No more users to load
                                    </div>
                                )}
                            </>
                        ) : (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: "3rem" }}>
                                <div style={{ fontSize: "3rem", marginBottom: "1rem", color: "#9AA6B5" }}><FaSearch /></div>
                                <div style={{ color: "#6F7D95", fontSize: "1rem", marginBottom: "0.5rem" }}>
                                    No users found
                                </div>
                                <div style={{ color: "#9AA6B5", fontSize: "0.9rem" }}>
                                    Try searching with different keywords
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Modal Footer - Fixed */}
                    <div
                        style={{
                            padding: "1.5rem 2rem",
                            borderTop: "1px solid #E9EDF4",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: "#F8FAFE",
                            flexShrink: 0,
                            boxShadow: "0 -4px 10px rgba(0,0,0,0.05)",
                        }}
                    >
                        <div style={{ color: "#0B1C33", fontWeight: 500 }}>
                            {selectedUsers.length} user(s) selected
                        </div>
                        <div style={{ display: "flex", gap: "1rem" }}>
                            <button
                                onClick={handleClose}
                                style={{
                                    background: "transparent",
                                    color: primaryBlue,
                                    border: `1px solid ${primaryBlue}`,
                                    borderRadius: "30px",
                                    padding: "0.6rem 1.5rem",
                                    fontSize: "0.95rem",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = primaryBlue;
                                    e.currentTarget.style.color = "white";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.color = primaryBlue;
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleInviteClick}
                                disabled={selectedUsers.length === 0 || addingUsers}
                                style={{
                                    background: selectedUsers.length === 0 ? "#9AA6B5" : primaryBlue,
                                    color: "white",
                                    border: "none",
                                    borderRadius: "30px",
                                    padding: "0.6rem 2rem",
                                    fontSize: "0.95rem",
                                    fontWeight: 600,
                                    cursor: selectedUsers.length === 0 ? "not-allowed" : "pointer",
                                    transition: "all 0.2s",
                                    opacity: selectedUsers.length === 0 ? 0.7 : 1,
                                    minWidth: "100px",
                                    boxShadow: selectedUsers.length > 0 ? "0 4px 10px rgba(0, 68, 198, 0.3)" : "none",
                                    pointerEvents: "auto",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                onMouseEnter={(e) => {
                                    if (selectedUsers.length > 0 && !addingUsers) {
                                        e.currentTarget.style.background = "#0033a0";
                                        e.currentTarget.style.transform = "scale(1.02)";
                                        e.currentTarget.style.boxShadow = "0 6px 15px rgba(0, 68, 198, 0.4)";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (selectedUsers.length > 0 && !addingUsers) {
                                        e.currentTarget.style.background = primaryBlue;
                                        e.currentTarget.style.transform = "scale(1)";
                                        e.currentTarget.style.boxShadow = "0 4px 10px rgba(0, 68, 198, 0.3)";
                                    }
                                }}
                            >
                                {addingUsers ? (
                                    <>
                                        <WiTime2 style={{ marginRight: "5px" }} /> Adding...
                                    </>
                                ) : (
                                    'INVITE'
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Add animation styles */}
                <style>
                    {`
              @keyframes slideIn {
                from {
                  transform: translateY(-20px);
                  opacity: 0;
                }
                to {
                  transform: translateY(0);
                  opacity: 1;
                }
              }
            `}
                </style>
            </div>
        </>
    );
};

export default InviteUserModal;